import { portalPaths } from '@provision/portal-routes';
import { useCallback, useEffect, useMemo } from 'react';
import { Address, Client, Conversation, useGroupRules, UserGroupRules } from 'src/graphql';
import { useGlobalStore } from 'src/store';
import useTabsStore from 'src/store/tabs';
import { useNavigateRoute } from 'src/utils/routing';

export enum TabType {
  Conversation = 'conversation',
  Site = 'site',
  Client = 'client',
}

export type ConversationTabEntity = {
  type: TabType.Conversation;
  entity: Pick<
    Conversation,
    'id' | 'conversationId' | 'friendlyId' | 'partnerId' | 'subject' | 'unseenCount'
  >;
};

export type SiteTabEntity = {
  type: TabType.Site;
  entity: Pick<Address, 'id' | 'addressId' | 'partnerId'>;
};

export type ClientTabEntity = {
  type: TabType.Client;
  entity: Pick<Client, 'id' | 'clientId' | 'partnerId'>;
};

export type TabEntity = ConversationTabEntity | SiteTabEntity | ClientTabEntity;

export type GlobalTab = {
  id: string;
  order: number;
  partnerId: number;
  metadata?: {
    customViewId?: string;
  };
  next: GlobalTab | null;
  prev: GlobalTab | null;
} & TabEntity;

export const useGlobalTabs = () => {
  const { addTab, tabs: persistedTabs, activeTab, setActiveTab, removeTab } = useTabsStore();
  const { navigate, pathnameWithoutPrefix } = useNavigateRoute();
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const { groupRules } = useGroupRules();
  const accessToTabsInProvision = groupRules[UserGroupRules.AccessToTabsInProvision];

  const tabs = useMemo(() => {
    return persistedTabs
      .filter(
        (tab) => tab.partnerId === partnerId || (isMultiTenancyMode && partnerId === null),
      )
      .sort((a, b) => a.order - b.order)
      .map<GlobalTab>((i, idx, arr) => {
        const prev = arr?.[idx - 1];
        const next = arr?.[idx + 1];
        return {
          ...i,
          next: next ? { ...next, next: null, prev: null } : null,
          prev: prev ? { ...prev, next: null, prev: null } : null,
        };
      });
  }, [isMultiTenancyMode, partnerId, persistedTabs]);

  const getExistingTab = useCallback(
    (value: TabEntity): GlobalTab | null => {
      switch (value.type) {
        case TabType.Conversation:
          return (
            tabs.find(
              (tab) => tab.type === value.type && tab.id === value.entity?.conversationId,
            ) || null
          );
        case TabType.Site:
          return (
            tabs.find(
              (tab) => tab.type === value.type && tab.id === value.entity?.addressId,
            ) || null
          );
        case TabType.Client:
          return (
            tabs.find((tab) => tab.type === value.type && tab.id === value.entity?.clientId) ||
            null
          );
        default:
          return null;
      }
    },
    [tabs],
  );

  const initTab = useCallback(
    (value: TabEntity, metadata?: GlobalTab['metadata']): GlobalTab => {
      const prevTab = tabs?.[tabs.length - 1] ?? null;

      const commonProps = {
        prev: prevTab,
        next: null,
        order: (prevTab?.order ?? 0) + 1,
        metadata,
      };

      switch (value.type) {
        case TabType.Conversation:
          return {
            id: value.entity.conversationId,
            type: TabType.Conversation,
            partnerId: value.entity.partnerId,
            entity: {
              id: value.entity.id,
              conversationId: value.entity.conversationId,
              partnerId: value.entity.partnerId,
              friendlyId: value.entity.friendlyId,
              unseenCount: value.entity.unseenCount,
              subject: value.entity.subject,
            },
            ...commonProps,
          };
        case TabType.Site:
          return {
            type: TabType.Site,
            id: value.entity.addressId,
            partnerId: value.entity.partnerId,
            entity: {
              id: value.entity.id,
              addressId: value.entity.addressId,
              partnerId: value.entity.partnerId,
            },
            ...commonProps,
          };
        case TabType.Client:
          return {
            type: TabType.Client,
            id: value.entity.clientId,
            partnerId: value.entity.partnerId,
            entity: {
              id: value.entity.id,
              clientId: value.entity.clientId,
              partnerId: value.entity.partnerId,
            },
            ...commonProps,
          };
      }
    },
    [tabs],
  );

  // openInTab allows to create a new tab if navigation to the resource is performed by external logic (ie navigation
  // with URL).
  const openInTab = useCallback(
    (value: TabEntity, metadata?: GlobalTab['metadata'], active = true) => {
      if (!accessToTabsInProvision) {
        return;
      }
      let tab = getExistingTab(value);

      if (!tab) {
        tab = initTab(value, metadata);
        addTab(tab);
      }
      if (active) {
        setActiveTab(tab);
      }
    },
    [addTab, getExistingTab, initTab, setActiveTab, accessToTabsInProvision],
  );

  // navigateTo is mechanism to switch between tabs, it should know how to navigate depending on tabs's type.
  const navigateToTab = useCallback(
    (tab: GlobalTab) => {
      switch (tab.type) {
        case TabType.Conversation:
          if (tab.entity.friendlyId) {
            navigate(portalPaths.conversation.full(tab.entity.friendlyId), {
              state: {
                customViewId: tab.metadata?.customViewId,
              },
            });
          }
          break;
        case TabType.Site:
        case TabType.Client:
          if (tab.entity.id) {
            navigate(portalPaths[tab.type].full(tab.entity.id));
          }
          break;
      }
      openInTab(tab);
    },
    [navigate, openInTab],
  );

  const findTab = useCallback(
    (id: string, type: TabEntity['type']) =>
      tabs.find((tab) => tab.id === id && tab.type === type),
    [tabs],
  );

  const closeLastTab = useCallback(
    (tab: GlobalTab) => {
      switch (tab.type) {
        case TabType.Conversation:
          if (
            tab.entity.friendlyId &&
            portalPaths.conversation.full(tab.entity.friendlyId) === pathnameWithoutPrefix
          ) {
            if (tab.metadata?.customViewId) {
              navigate(portalPaths.entity.selected('conversation', tab.metadata.customViewId));
            } else {
              navigate(
                portalPaths.entity.all({
                  entityName: 'conversations',
                }),
              );
            }
          }
          break;
        case TabType.Site:
          if (
            tab.entity.addressId &&
            portalPaths.site.full(tab.entity.addressId) === pathnameWithoutPrefix
          ) {
            if (tab.metadata?.customViewId) {
              navigate(portalPaths.entity.selected('site', tab.metadata.customViewId));
            } else {
              navigate(
                portalPaths.entity.all({
                  entityName: 'site',
                }),
              );
            }
          }
          break;
        case TabType.Client:
          if (
            tab.entity.clientId &&
            portalPaths.client.full(tab.entity.clientId) === pathnameWithoutPrefix
          ) {
            if (tab.metadata?.customViewId) {
              navigate(portalPaths.entity.selected('client', tab.metadata.customViewId));
            } else {
              navigate(
                portalPaths.entity.all({
                  entityName: 'client',
                }),
              );
            }
          }
          break;
      }
      setActiveTab(null);
    },
    [pathnameWithoutPrefix, navigate, setActiveTab],
  );

  const closeTab = useCallback(
    (tab: GlobalTab, navigateOnClose = true) => {
      removeTab(tab.id);
      if (navigateOnClose && tab.id === activeTab?.id) {
        const goToTab = tab.next || tab.prev;
        if (goToTab) {
          navigateToTab(goToTab);
        } else {
          closeLastTab(tab);
        }
      }
    },
    [activeTab?.id, closeLastTab, navigateToTab, removeTab],
  );

  const closeTabWith = useCallback(
    (predicate: (tab: GlobalTab) => boolean, navigateOnClose = true) => {
      tabs.filter(predicate).forEach((it) => closeTab(it, navigateOnClose));
    },
    [closeTab, tabs],
  );

  // handle navigation away to other views that should set all tabs as inactive.
  useEffect(() => {
    const tab = tabs.find((tab) => tab.id === activeTab?.id && tab.type === activeTab?.type);
    if (!tab) {
      setActiveTab(null);
      return;
    }

    switch (tab?.type) {
      case TabType.Conversation:
        if (
          tab.entity?.friendlyId &&
          !pathnameWithoutPrefix.endsWith(
            portalPaths.conversation.full(tab.entity?.friendlyId),
          )
        ) {
          setActiveTab(null);
        }
        break;
      case TabType.Site:
      case TabType.Client:
        if (
          tab.entity?.id &&
          portalPaths[tab?.type].full(tab.entity?.id) !== pathnameWithoutPrefix
        ) {
          setActiveTab(null);
        }
        break;
    }
  }, [activeTab?.id, activeTab?.type, pathnameWithoutPrefix, setActiveTab, tabs]);

  return {
    activeTab,
    tabs,
    openInTab,
    navigateToTab,
    findTab,
    closeTab,
    closeTabWith,
  };
};
